import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  X, Sliders, Plus, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, Camera, Trash2,
  GripVertical, ArrowUp, ArrowDown, Wind,
  CheckCircle2, RotateCcw, Pencil, Check,
  Download, Target, Eye,
} from 'lucide-react';
import { useToast } from './Toast';
import TrainingDiaryModal from './TrainingDiaryModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABELS = {
  vapengarderobe: 'Våpengarderobe',
  sikte_type: 'Sikte type',
  ammunisjons_logg: 'Ammunisjons-logg',
  serie_split: 'Serie-split & X',
  klikktelling: 'Sikte klikktelling',
  gruppestorrelse: 'Gruppestørrelse',
  dagsform: 'Dagsform',
  hurtig_tagger: 'Hurtig-tagger',
  tidsbruk_timer: 'Tidsbruk / Timer',
  progresjonsgraf: 'Progresjonsgraf',
  bildeopplasting: 'Bildeopplasting',
  personlige_notater: 'Personlige notater',
};

const DEFAULT_MODULES = [
  { id: 'tidsbruk_timer', enabled: true, order: 0 },
  { id: 'vapengarderobe', enabled: true, order: 1 },
  { id: 'sikte_type', enabled: true, order: 2 },
  { id: 'ammunisjons_logg', enabled: true, order: 3 },
  { id: 'serie_split', enabled: true, order: 4 },
  { id: 'bildeopplasting', enabled: true, order: 5 },
  { id: 'personlige_notater', enabled: true, order: 6 },
  { id: 'klikktelling', enabled: true, order: 7 },
  { id: 'gruppestorrelse', enabled: true, order: 8 },
  { id: 'dagsform', enabled: true, order: 9 },
  { id: 'hurtig_tagger', enabled: true, order: 10 },
  { id: 'progresjonsgraf', enabled: true, order: 11 },
];

const DEFAULT_TAGS = ['Trening', 'Stevne', 'Utendørs', 'Vind', 'God dag'];
const DEFAULT_SIGHT_TAGS = ['Jernsikte', 'Fiber-tråd', 'Optisk sikte', 'Rødpunktsikte'];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

// Format seconds to MM:SS.cc
const formatTime = (totalSeconds) => {
  const mm = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const ss = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  const cc = Math.floor((totalSeconds * 100) % 100).toString().padStart(2, '0');
  return `${mm}:${ss}.${cc}`;
};

const SkytedagbokModal = ({ session, isOpen, onClose, onUpdate }) => {
  const { addToast } = useToast();

  // ---- Mode (PRO / ENKEL) ----
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem('skytedagbok_mode') || 'pro';
    } catch {
      return 'pro';
    }
  });
  const switchMode = (m) => {
    setMode(m);
    try {
      localStorage.setItem('skytedagbok_mode', m);
    } catch {
      /* ignore */
    }
  };

  // ---- Preferences (Skreddersy) ----
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [trendPeriod, setTrendPeriod] = useState(30);
  const [showSkreddersy, setShowSkreddersy] = useState(false);

  // ---- User collections ----
  const [weapons, setWeapons] = useState([]);
  const [ammoList, setAmmoList] = useState([]);
  const [customTags, setCustomTags] = useState([]);
  const [customSights, setCustomSights] = useState([]);
  const [trendData, setTrendData] = useState([]);

  // ---- Diary form state ----
  const [diaryWeaponId, setDiaryWeaponId] = useState(null);
  const [diaryAmmoId, setDiaryAmmoId] = useState(null);
  const [series, setSeries] = useState([{ poeng: '', inner: '', time: 0, splits: [], hits: [], target_image: null }]);
  const [clicksHeightDir, setClicksHeightDir] = useState('down');
  const [clicksHeightCount, setClicksHeightCount] = useState(0);
  const [clicksSideDir, setClicksSideDir] = useState('left');
  const [clicksSideCount, setClicksSideCount] = useState(0);
  const [clicksHistory, setClicksHistory] = useState([]); // [{ts, summary, items}]
  const [persistedClicksHistory, setPersistedClicksHistory] = useState([]);
  const clicksSessionRef = useRef({ startedAt: null, items: { up: 0, down: 0, left: 0, right: 0 }, timer: null });
  const [dagsform, setDagsform] = useState(0); // 1-5
  const [windLevel, setWindLevel] = useState(null); // 0=Stille, 1=Lite, 2=Moderat, 3=Mye
  const [activeTags, setActiveTags] = useState([]);
  const [activeSightTags, setActiveSightTags] = useState([]);
  const [groupSizeMm, setGroupSizeMm] = useState('');
  const [groupSizeCaliber, setGroupSizeCaliber] = useState('');
  const [groupSizeShots, setGroupSizeShots] = useState('');
  const [groupSizeNotes, setGroupSizeNotes] = useState('');
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  const [splitTimes, setSplitTimes] = useState([]);
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Inline add forms
  const [addingWeapon, setAddingWeapon] = useState(false);
  const [newWeaponName, setNewWeaponName] = useState('');
  const [newWeaponCal, setNewWeaponCal] = useState('');
  const [addingAmmo, setAddingAmmo] = useState(false);
  const [newAmmoName, setNewAmmoName] = useState('');
  const [newAmmoBatch, setNewAmmoBatch] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  // Drag-and-drop
  const [dragId, setDragId] = useState(null);

  // ---- Load preferences and collections on open ----
  useEffect(() => {
    if (!isOpen) return;
    loadAllData();
    // Pre-fill from existing session
    if (session) {
      setDiaryWeaponId(session.diary_weapon_id || null);
      setDiaryAmmoId(session.diary_ammo_id || null);
      setSeries(
        session.series && session.series.length > 0
          ? session.series.map((s) => ({
              poeng: s.poeng ?? '',
              inner: s.inner ?? '',
              time: s.time ?? 0,
              splits: Array.isArray(s.splits) ? s.splits : [],
              hits: Array.isArray(s.hits) ? s.hits : [],
              target_image: s.target_image || null,
            }))
          : [{ poeng: '', inner: '', time: 0, splits: [], hits: [], target_image: null }]
      );
      setClicksHeightDir(session.clicks_height_dir || 'down');
      setClicksHeightCount(session.clicks_height_count || 0);
      setClicksSideDir(session.clicks_side_dir || 'left');
      setClicksSideCount(session.clicks_side_count || 0);
      setClicksHistory(Array.isArray(session.clicks_history) ? session.clicks_history : []);
      setDagsform(session.dagsform || 0);
      setWindLevel(typeof session.wind_level === 'number' ? session.wind_level : null);
      setActiveTags(session.tags || []);
      setActiveSightTags(session.sight_tags || []);
      setGroupSizeMm(
        session.group_size_mm != null && Number.isFinite(Number(session.group_size_mm))
          ? session.group_size_mm
          : ''
      );
      setGroupSizeCaliber(
        session.group_size_caliber_mm != null && Number.isFinite(Number(session.group_size_caliber_mm))
          ? session.group_size_caliber_mm
          : ''
      );
      setGroupSizeShots(
        session.group_size_shots != null && Number.isFinite(Number(session.group_size_shots))
          ? session.group_size_shots
          : ''
      );
      setGroupSizeNotes(session.group_size_notes || '');
      setTimerSeconds(session.timer_seconds || 0);
      setNotes(session.training_notes || '');
      const s3 = session.target_images_s3_urls || [];
      const orig = session.target_images || [];
      setImages((s3.length > 0 ? s3 : orig).filter(Boolean));
    }
    // eslint-disable-next-line
  }, [isOpen, session?.id]);

  // Load trend when period changes
  useEffect(() => {
    if (!isOpen) return;
    loadTrend(trendPeriod);
    // eslint-disable-next-line
  }, [trendPeriod, isOpen]);

  // Cleanup timer on close
  useEffect(() => {
    if (!isOpen && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setTimerRunning(false);
    }
  }, [isOpen]);

  const loadAllData = async () => {
    try {
      const [prefRes, weaponsRes, ammoRes, tagsRes, sightsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/diary/preferences`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/api/diary/weapons`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/api/diary/ammo`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/api/diary/tags`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/api/diary/sights`, { headers: authHeaders() }),
      ]);
      const loadedModules = prefRes.data?.modules || DEFAULT_MODULES;
      // Merge with defaults to ensure all 10 are present
      const ids = new Set(loadedModules.map((m) => m.id));
      const merged = [...loadedModules];
      DEFAULT_MODULES.forEach((d) => {
        if (!ids.has(d.id)) merged.push(d);
      });
      setModules(merged.sort((a, b) => a.order - b.order));
      setTrendPeriod(prefRes.data?.trend_period_days || 30);
      setWeapons(weaponsRes.data || []);
      setAmmoList(ammoRes.data || []);
      setCustomTags(tagsRes.data || []);
      setCustomSights(sightsRes.data || []);
      // Auto-import weapons from Min profil (silently) each time modal opens
      try {
        const importRes = await axios.post(
          `${API_BASE}/api/diary/weapons/import-from-profile`,
          {},
          { headers: authHeaders() }
        );
        if (importRes.data?.imported > 0 && Array.isArray(importRes.data.weapons)) {
          setWeapons((prev) => [...prev, ...importRes.data.weapons]);
        }
      } catch (importErr) {
        console.error('Silent weapon import failed:', importErr);
      }
    } catch (err) {
      console.error('Could not load Skytedagbok data:', err);
    }
  };

  const loadTrend = async (days) => {
    try {
      const res = await axios.get(`${API_BASE}/api/diary/trend?days=${days}`, {
        headers: authHeaders(),
      });
      setTrendData(res.data?.points || []);
    } catch (err) {
      console.error('Could not load trend:', err);
      setTrendData([]);
    }
  };

  // ---- Skreddersy actions ----
  // Auto-save preferences whenever modules or trendPeriod change in Skreddersy overlay.
  const skipNextAutoSave = useRef(true); // Skip first save on initial load
  const autoSaveTimer = useRef(null);
  const [autoSaveState, setAutoSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  useEffect(() => {
    if (!isOpen) return;
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    setAutoSaveState('saving');
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await axios.put(
          `${API_BASE}/api/diary/preferences`,
          { modules, trend_period_days: trendPeriod },
          { headers: authHeaders() }
        );
        setAutoSaveState('saved');
        setTimeout(() => setAutoSaveState('idle'), 1500);
      } catch (err) {
        console.error('Auto-save preferences failed:', err);
        setAutoSaveState('error');
      }
    }, 400);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line
  }, [modules, trendPeriod]);

  // Reset skip flag when modal re-opens (so loadAllData triggers without immediate save)
  useEffect(() => {
    if (isOpen) skipNextAutoSave.current = true;
  }, [isOpen]);

  const toggleModule = (id) => {
    setModules((prev) =>
      prev.map((m) => (m.id === id ? { ...m, enabled: !m.enabled } : m))
    );
  };

  const moveModule = (id, dir) => {
    setModules((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((m) => m.id === id);
      if (idx === -1) return prev;
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= sorted.length) return prev;
      [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
      return sorted.map((m, i) => ({ ...m, order: i }));
    });
  };

  const handleDragStart = (id) => setDragId(id);
  const handleDragOver = (e, overId) => {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    setModules((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const fromIdx = sorted.findIndex((m) => m.id === dragId);
      const toIdx = sorted.findIndex((m) => m.id === overId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = sorted.splice(fromIdx, 1);
      sorted.splice(toIdx, 0, moved);
      return sorted.map((m, i) => ({ ...m, order: i }));
    });
  };
  const handleDragEnd = () => setDragId(null);

  // Touch support for drag-and-drop (mobile)
  const touchDragId = useRef(null);
  const handleTouchStart = (id) => {
    touchDragId.current = id;
    setDragId(id);
  };
  const handleTouchMove = (e) => {
    if (!touchDragId.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    const row = target?.closest('[data-skreddersy-row]');
    if (!row) return;
    const overId = row.getAttribute('data-skreddersy-row');
    if (!overId || overId === touchDragId.current) return;
    setModules((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const fromIdx = sorted.findIndex((m) => m.id === touchDragId.current);
      const toIdx = sorted.findIndex((m) => m.id === overId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = sorted.splice(fromIdx, 1);
      sorted.splice(toIdx, 0, moved);
      return sorted.map((m, i) => ({ ...m, order: i }));
    });
  };
  const handleTouchEnd = () => {
    touchDragId.current = null;
    setDragId(null);
  };

  // ---- Auto-save diary fields (debounced) ----
  const skipNextDiaryAutoSave = useRef(true);
  const diaryAutoSaveTimer = useRef(null);
  const [diarySaveState, setDiarySaveState] = useState('idle');
  const saveSuccessCallbackFired = useRef(false);

  // Reset skip flag when modal opens (so initial pre-fill doesn't trigger save)
  useEffect(() => {
    if (isOpen) {
      skipNextDiaryAutoSave.current = true;
      saveSuccessCallbackFired.current = false;
    }
  }, [isOpen, session?.id]);

  // Load persisted sight-clicks history from earlier training sessions when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ limit: '200' });
        if (diaryWeaponId) params.append('diary_weapon_id', diaryWeaponId);
        const res = await axios.get(`${API_BASE}/api/training/sessions/sight-clicks-history?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res?.data?.history) {
          setPersistedClicksHistory(res.data.history);
        }
      } catch (e) {
        // Silent — history is a nice-to-have; component still shows current session
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, diaryWeaponId, session?.id]);

  useEffect(() => {
    if (!isOpen || !session?.id) return;
    if (skipNextDiaryAutoSave.current) {
      skipNextDiaryAutoSave.current = false;
      return;
    }
    if (diaryAutoSaveTimer.current) clearTimeout(diaryAutoSaveTimer.current);
    setDiarySaveState('saving');
    diaryAutoSaveTimer.current = setTimeout(async () => {
      try {
        const cleanedSeries = series
          .filter((s) => s.poeng !== '' || s.inner !== '' || (s.time && s.time > 0) || s.target_image || (Array.isArray(s.hits) && s.hits.length > 0))
          .map((s) => ({
            poeng: s.poeng === '' ? 0 : Number(s.poeng),
            inner: s.inner === '' ? 0 : Number(s.inner),
            time: s.time || 0,
            splits: Array.isArray(s.splits) ? s.splits : [],
            hits: Array.isArray(s.hits) ? s.hits : [],
            target_image: s.target_image || null,
          }));
        await axios.put(
          `${API_BASE}/api/training/sessions/${session.id}/diary`,
          {
            training_notes: notes,
            target_images: images,
            diary_weapon_id: diaryWeaponId,
            diary_ammo_id: diaryAmmoId,
            series: cleanedSeries,
            clicks_height_dir: clicksHeightCount > 0 ? clicksHeightDir : null,
            clicks_height_count: clicksHeightCount,
            clicks_side_dir: clicksSideCount > 0 ? clicksSideDir : null,
            clicks_side_count: clicksSideCount,
            dagsform: dagsform || null,
            wind_level: windLevel === null || windLevel === undefined ? null : windLevel,
            tags: activeTags,
            sight_tags: activeSightTags,
            group_size_mm: (groupSizeMm === '' || !Number.isFinite(Number(groupSizeMm))) ? null : Number(groupSizeMm),
            group_size_caliber_mm: (groupSizeCaliber === '' || !Number.isFinite(Number(groupSizeCaliber))) ? null : Number(groupSizeCaliber),
            group_size_shots: (groupSizeShots === '' || !Number.isFinite(Number(groupSizeShots))) ? null : Number(groupSizeShots),
            group_size_notes: groupSizeNotes,
            timer_seconds: timerSeconds,
          },
          { headers: authHeaders() }
        );
        setDiarySaveState('saved');
        setTimeout(() => setDiarySaveState('idle'), 1500);
        // First successful save → trigger parent refresh so Skytedagbok-knappen
        // umiddelbart går fra grå til grønn i treningsloggen
        if (!saveSuccessCallbackFired.current) {
          saveSuccessCallbackFired.current = true;
          try { onUpdate?.(); } catch (_e) { /* noop */ }
        }
      } catch (err) {
        console.error('Diary auto-save failed:', err);
        setDiarySaveState('error');
      }
    }, 800);
    return () => {
      if (diaryAutoSaveTimer.current) clearTimeout(diaryAutoSaveTimer.current);
    };
    // eslint-disable-next-line
  }, [
    diaryWeaponId, diaryAmmoId, series,
    clicksHeightDir, clicksHeightCount, clicksSideDir, clicksSideCount,
    clicksHistory,
    dagsform, windLevel, activeTags, activeSightTags,
    groupSizeMm, groupSizeCaliber, groupSizeShots, groupSizeNotes,
    notes, images,
  ]);

  const saveSkreddersy = async () => {
    // Auto-save already handles persistence; this button just closes overlay.
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    try {
      await axios.put(
        `${API_BASE}/api/diary/preferences`,
        { modules, trend_period_days: trendPeriod },
        { headers: authHeaders() }
      );
      addToast('Innstillinger lagret', 'success');
      setShowSkreddersy(false);
    } catch (err) {
      console.error('Failed to save preferences:', err);
      addToast('Kunne ikke lagre innstillinger', 'error');
    }
  };

  // ---- Weapon actions ----
  const addWeapon = async () => {
    if (!newWeaponName.trim()) return;
    try {
      const res = await axios.post(
        `${API_BASE}/api/diary/weapons`,
        { name: newWeaponName.trim(), caliber: newWeaponCal.trim() || null },
        { headers: authHeaders() }
      );
      setWeapons((prev) => [...prev, res.data]);
      setDiaryWeaponId(res.data.id);
      setNewWeaponName('');
      setNewWeaponCal('');
      setAddingWeapon(false);
    } catch (err) {
      console.error('Add weapon error:', err);
      addToast('Kunne ikke legge til våpen', 'error');
    }
  };

  const removeWeapon = async (id) => {
    if (!window.confirm('Slette dette våpenet fra listen?')) return;
    try {
      await axios.delete(`${API_BASE}/api/diary/weapons/${id}`, { headers: authHeaders() });
      setWeapons((prev) => prev.filter((w) => w.id !== id));
      if (diaryWeaponId === id) setDiaryWeaponId(null);
    } catch (err) {
      console.error('Delete weapon error:', err);
    }
  };

  const [importingWeapons, setImportingWeapons] = useState(false);
  const importWeaponsFromProfile = async () => {
    setImportingWeapons(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/diary/weapons/import-from-profile`,
        {},
        { headers: authHeaders() }
      );
      const { imported, skipped, weapons: newWeapons } = res.data;
      if (imported > 0) {
        setWeapons((prev) => [...prev, ...newWeapons]);
        addToast(
          `Importerte ${imported} våpen${skipped > 0 ? ` (${skipped} dupliserte)` : ''}`,
          'success'
        );
      } else if (skipped > 0) {
        addToast(`Ingen nye våpen å importere (${skipped} finnes allerede)`, 'info');
      } else {
        addToast('Ingen våpen funnet i Min profil', 'info');
      }
    } catch (err) {
      console.error('Import weapons error:', err);
      addToast('Kunne ikke importere våpen', 'error');
    } finally {
      setImportingWeapons(false);
    }
  };

  const [editingWeaponId, setEditingWeaponId] = useState(null);
  const [editWeaponName, setEditWeaponName] = useState('');
  const [editWeaponCal, setEditWeaponCal] = useState('');
  const startEditWeapon = (w) => {
    setEditingWeaponId(w.id);
    setEditWeaponName(w.name || '');
    setEditWeaponCal(w.caliber || '');
  };
  const cancelEditWeapon = () => {
    setEditingWeaponId(null);
    setEditWeaponName('');
    setEditWeaponCal('');
  };
  const saveEditWeapon = async () => {
    if (!editingWeaponId || !editWeaponName.trim()) return cancelEditWeapon();
    try {
      const res = await axios.put(
        `${API_BASE}/api/diary/weapons/${editingWeaponId}`,
        { name: editWeaponName.trim(), caliber: editWeaponCal.trim() || null },
        { headers: authHeaders() }
      );
      setWeapons((prev) => prev.map((w) => (w.id === editingWeaponId ? res.data : w)));
      cancelEditWeapon();
    } catch (err) {
      console.error('Edit weapon error:', err);
      addToast('Kunne ikke lagre våpen', 'error');
    }
  };

  // ---- Ammo actions ----
  const addAmmo = async () => {
    if (!newAmmoName.trim()) return;
    try {
      const res = await axios.post(
        `${API_BASE}/api/diary/ammo`,
        { name: newAmmoName.trim(), batch_number: newAmmoBatch.trim() || null },
        { headers: authHeaders() }
      );
      setAmmoList((prev) => [...prev, res.data]);
      setDiaryAmmoId(res.data.id);
      setNewAmmoName('');
      setNewAmmoBatch('');
      setAddingAmmo(false);
    } catch (err) {
      console.error('Add ammo error:', err);
      addToast('Kunne ikke legge til ammunisjon', 'error');
    }
  };

  const removeAmmo = async (id) => {
    if (!window.confirm('Slette denne ammunisjonen fra listen?')) return;
    try {
      await axios.delete(`${API_BASE}/api/diary/ammo/${id}`, { headers: authHeaders() });
      setAmmoList((prev) => prev.filter((a) => a.id !== id));
      if (diaryAmmoId === id) setDiaryAmmoId(null);
    } catch (err) {
      console.error('Delete ammo error:', err);
    }
  };

  const [editingAmmoId, setEditingAmmoId] = useState(null);
  const [editAmmoName, setEditAmmoName] = useState('');
  const [editAmmoBatch, setEditAmmoBatch] = useState('');
  const startEditAmmo = (a) => {
    setEditingAmmoId(a.id);
    setEditAmmoName(a.name || '');
    setEditAmmoBatch(a.batch_number || '');
  };
  const cancelEditAmmo = () => {
    setEditingAmmoId(null);
    setEditAmmoName('');
    setEditAmmoBatch('');
  };
  const saveEditAmmo = async () => {
    if (!editingAmmoId || !editAmmoName.trim()) return cancelEditAmmo();
    try {
      const res = await axios.put(
        `${API_BASE}/api/diary/ammo/${editingAmmoId}`,
        { name: editAmmoName.trim(), batch_number: editAmmoBatch.trim() || null },
        { headers: authHeaders() }
      );
      setAmmoList((prev) => prev.map((a) => (a.id === editingAmmoId ? res.data : a)));
      cancelEditAmmo();
    } catch (err) {
      console.error('Edit ammo error:', err);
      addToast('Kunne ikke lagre ammunisjon', 'error');
    }
  };

  // ---- Tag actions ----
  const toggleTag = (name) => {
    setActiveTags((prev) => (prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]));
  };

  const addCustomTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await axios.post(
        `${API_BASE}/api/diary/tags`,
        { name: newTagName.trim() },
        { headers: authHeaders() }
      );
      setCustomTags((prev) => {
        if (prev.find((t) => t.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setActiveTags((prev) => (prev.includes(res.data.name) ? prev : [...prev, res.data.name]));
      setNewTagName('');
      setAddingTag(false);
    } catch (err) {
      console.error('Add tag error:', err);
    }
  };

  // Edit existing custom tag
  const [editingTagId, setEditingTagId] = useState(null);
  const [editTagValue, setEditTagValue] = useState('');
  const startEditTag = (tag) => {
    setEditingTagId(tag.id);
    setEditTagValue(tag.name);
  };
  const cancelEditTag = () => {
    setEditingTagId(null);
    setEditTagValue('');
  };
  const saveEditTag = async () => {
    const newName = editTagValue.trim();
    if (!newName || !editingTagId) return cancelEditTag();
    try {
      const res = await axios.put(
        `${API_BASE}/api/diary/tags/${editingTagId}`,
        { name: newName },
        { headers: authHeaders() }
      );
      const oldTag = customTags.find((t) => t.id === editingTagId);
      const oldName = oldTag?.name;
      setCustomTags((prev) =>
        prev.map((t) => (t.id === editingTagId ? res.data : t))
      );
      // Update activeTags if old name was active
      if (oldName && oldName !== newName) {
        setActiveTags((prev) =>
          prev.map((t) => (t === oldName ? newName : t))
        );
      }
      cancelEditTag();
    } catch (err) {
      console.error('Edit tag error:', err);
      addToast(err.response?.data?.detail || 'Kunne ikke redigere tagg', 'error');
    }
  };

  const removeCustomTag = async (tag) => {
    if (!window.confirm(`Slette taggen "${tag.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/api/diary/tags/${tag.id}`, { headers: authHeaders() });
      setCustomTags((prev) => prev.filter((t) => t.id !== tag.id));
      setActiveTags((prev) => prev.filter((t) => t !== tag.name));
    } catch (err) {
      console.error('Delete tag error:', err);
    }
  };

  // ---- Sight actions ----
  const toggleSightTag = (name) => {
    setActiveSightTags((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );
  };

  const [addingSight, setAddingSight] = useState(false);
  const [newSightName, setNewSightName] = useState('');
  const addCustomSight = async () => {
    if (!newSightName.trim()) return;
    try {
      const res = await axios.post(
        `${API_BASE}/api/diary/sights`,
        { name: newSightName.trim() },
        { headers: authHeaders() }
      );
      setCustomSights((prev) => {
        if (prev.find((t) => t.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setActiveSightTags((prev) =>
        prev.includes(res.data.name) ? prev : [...prev, res.data.name]
      );
      setNewSightName('');
      setAddingSight(false);
    } catch (err) {
      console.error('Add sight error:', err);
    }
  };
  const removeCustomSight = async (sight) => {
    if (!window.confirm(`Slette sikte-tag "${sight.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/api/diary/sights/${sight.id}`, {
        headers: authHeaders(),
      });
      setCustomSights((prev) => prev.filter((t) => t.id !== sight.id));
      setActiveSightTags((prev) => prev.filter((t) => t !== sight.name));
    } catch (err) {
      console.error('Delete sight error:', err);
    }
  };

  // ---- Series actions ----
  const updateSeries = (idx, field, value) => {
    setSeries((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        // Only validate poeng/inner: must not go below 0
        if ((field === 'poeng' || field === 'inner') && value !== '') {
          const num = Number(value);
          if (Number.isFinite(num) && num < 0) {
            return { ...s, [field]: 0 };
          }
        }
        return { ...s, [field]: value };
      })
    );
  };
  const addSeries = () => {
    setSeries((prev) => [...prev, { poeng: '', inner: '', time: 0, splits: [], hits: [], target_image: null }]);
    // Reset timer for the new series
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimerRunning(false);
    setTimerSeconds(0);
    setSplitTimes([]);
  };
  const removeSeries = (idx) =>
    setSeries((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // ---- Click counter ----
  const recordClickInSession = (dir) => {
    const sess = clicksSessionRef.current;
    if (!sess.startedAt) sess.startedAt = Date.now();
    sess.items = { ...sess.items, [dir]: (sess.items[dir] || 0) + 1 };
    if (sess.timer) clearTimeout(sess.timer);
    sess.timer = setTimeout(() => {
      const items = sess.items;
      const labels = { up: 'OPP', down: 'NED', left: 'VENSTRE', right: 'HØYRE' };
      const numWords = (n) => {
        if (n === 1) return 'Ett';
        if (n === 2) return 'To';
        if (n === 3) return 'Tre';
        if (n === 4) return 'Fire';
        if (n === 5) return 'Fem';
        return String(n);
      };
      const parts = [];
      ['up', 'down', 'left', 'right'].forEach((k) => {
        if (items[k] > 0) {
          parts.push(`${numWords(items[k])} klikk ${labels[k]}`);
        }
      });
      const summary = parts.join(' - ');
      const startedAt = sess.startedAt;
      setClicksHistory((prev) => [
        { at: new Date(startedAt).toISOString(), summary, items: { ...items } },
        ...prev,
      ]);
      clicksSessionRef.current = { startedAt: null, items: { up: 0, down: 0, left: 0, right: 0 }, timer: null };
    }, 4000);
  };

  const setClickHeight = (dir) => {
    if (clicksHeightDir === dir) {
      setClicksHeightCount((n) => n + 1);
    } else {
      setClicksHeightDir(dir);
      setClicksHeightCount(1);
    }
    recordClickInSession(dir);
  };
  const setClickSide = (dir) => {
    if (clicksSideDir === dir) {
      setClicksSideCount((n) => n + 1);
    } else {
      setClicksSideDir(dir);
      setClicksSideCount(1);
    }
    recordClickInSession(dir);
  };

  // ---- Timer ----
  const startTimer = () => {
    if (timerRunning) return;
    const start = Date.now() - timerSeconds * 1000;
    setTimerRunning(true);
    timerRef.current = setInterval(() => {
      setTimerSeconds((Date.now() - start) / 1000);
    }, 50);
  };
  const [expandedSerieIdx, setExpandedSerieIdx] = useState(null);
  const [punchSerieIdx, setPunchSerieIdx] = useState(null); // Hvilken serie åpen i Punch-poeng-modal
  const [serieImageLightbox, setSerieImageLightbox] = useState(null); // url for lightbox-preview
  const [uploadingSerieIdx, setUploadingSerieIdx] = useState(null);
  const openPunchPoeng = (idx) => setPunchSerieIdx(idx);

  // Shared helper: build full diary payload (with optional override series) and PUT synchronously
  const buildDiaryPayload = (seriesOverride) => {
    const srcSeries = Array.isArray(seriesOverride) ? seriesOverride : series;
    const cleanedSeries = srcSeries
      .filter((s) => s.poeng !== '' || s.inner !== '' || (s.time && s.time > 0) || s.target_image || (Array.isArray(s.hits) && s.hits.length > 0))
      .map((s) => ({
        poeng: s.poeng === '' ? 0 : Number(s.poeng),
        inner: s.inner === '' ? 0 : Number(s.inner),
        time: s.time || 0,
        splits: Array.isArray(s.splits) ? s.splits : [],
        hits: Array.isArray(s.hits) ? s.hits : [],
        target_image: s.target_image || null,
      }));
    return {
      training_notes: notes,
      target_images: images,
      diary_weapon_id: diaryWeaponId,
      diary_ammo_id: diaryAmmoId,
      series: cleanedSeries,
      clicks_height_dir: clicksHeightCount > 0 ? clicksHeightDir : null,
      clicks_height_count: clicksHeightCount,
      clicks_side_dir: clicksSideCount > 0 ? clicksSideDir : null,
      clicks_side_count: clicksSideCount,
      clicks_history: clicksHistory,
      dagsform: dagsform || null,
      wind_level: windLevel === null || windLevel === undefined ? null : windLevel,
      tags: activeTags,
      sight_tags: activeSightTags,
      group_size_mm: (groupSizeMm === '' || !Number.isFinite(Number(groupSizeMm))) ? null : Number(groupSizeMm),
      group_size_caliber_mm: (groupSizeCaliber === '' || !Number.isFinite(Number(groupSizeCaliber))) ? null : Number(groupSizeCaliber),
      group_size_shots: (groupSizeShots === '' || !Number.isFinite(Number(groupSizeShots))) ? null : Number(groupSizeShots),
      group_size_notes: groupSizeNotes,
      timer_seconds: timerSeconds,
    };
  };

  const flushDiarySaveNow = async (seriesOverride) => {
    // Cancel any pending debounced autosave
    if (diaryAutoSaveTimer.current) {
      clearTimeout(diaryAutoSaveTimer.current);
      diaryAutoSaveTimer.current = null;
    }
    if (!session?.id) return;
    try {
      setDiarySaveState('saving');
      await axios.put(
        `${API_BASE}/api/training/sessions/${session.id}/diary`,
        buildDiaryPayload(seriesOverride),
        { headers: authHeaders() }
      );
      setDiarySaveState('saved');
      setTimeout(() => setDiarySaveState('idle'), 1500);
      if (!saveSuccessCallbackFired.current) {
        saveSuccessCallbackFired.current = true;
        try { onUpdate?.(); } catch (_e) { /* noop */ }
      }
    } catch (err) {
      console.error('[Diary] Synchronous save failed:', err);
      setDiarySaveState('error');
    }
  };

  const commitPunchPoeng = async (idx, incomingHits) => {
    // Log slik at vi kan se at funksjonen trigges (debug for live/prod)
    console.log('[PunchPoeng] commitPunchPoeng called', { idx, hitsLen: (incomingHits || []).length });
    const hitsArr = Array.isArray(incomingHits) ? incomingHits : [];
    // hitsArr: array av verdier (0-10 eller 'X' for inner-ti). X teller som 10 poeng + 1 inner.
    const poeng = hitsArr.reduce((sum, h) => sum + (h === 'X' ? 10 : Number(h) || 0), 0);
    const inner = hitsArr.filter(h => h === 'X').length;
    const updatedSeries = series.map((s, i) =>
      i === idx ? { ...s, poeng: String(poeng), inner: String(inner), hits: hitsArr } : s
    );
    setSeries(updatedSeries);
    setPunchSerieIdx(null); // lukk modalen umiddelbart
    // Synkron lagring til backend med den oppdaterte serien (hopper over debounce)
    await flushDiarySaveNow(updatedSeries);
  };
  const uploadSerieImage = async (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast('Kun bildefiler er tillatt', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      addToast('Bildet er for stort (maks 10MB)', 'error');
      return;
    }
    setUploadingSerieIdx(idx);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await axios.post(
        `${API_BASE}/api/training/sessions/${session.id}/upload-serie-image`,
        { image_base64: base64 },
        { headers: authHeaders() }
      );
      if (res.data.success) {
        const url = res.data.image_url;
        setSeries((prev) => prev.map((s, i) => i === idx ? { ...s, target_image: url } : s));
        addToast('Blinkbilde lagret', 'success');
      }
    } catch (err) {
      console.error('Serie-bilde upload error:', err);
      addToast('Kunne ikke laste opp bilde', 'error');
    } finally {
      setUploadingSerieIdx(null);
    }
  };
  const clearSerieImage = (idx) => {
    setSeries((prev) => prev.map((s, i) => i === idx ? { ...s, target_image: null } : s));
  };
  const splitTimer = () => {
    if (!timerRunning) return;
    setSplitTimes((prev) => [...prev, timerSeconds]);
    // Attach split-time to last series
    setSeries((prev) => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      const last = updated[lastIdx];
      updated[lastIdx] = {
        ...last,
        splits: [...(last.splits || []), timerSeconds],
      };
      return updated;
    });
  };
  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setTimerRunning(false);
    // Attach current time to last series
    if (timerSeconds > 0) {
      setSeries((prev) => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        updated[lastIdx] = { ...updated[lastIdx], time: timerSeconds };
        return updated;
      });
    }
  };
  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setTimerRunning(false);
    setTimerSeconds(0);
    setSplitTimes([]);
  };

  // ---- Image upload ----
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        addToast('Kun bildefiler er tillatt', 'error');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        addToast('Bildet er for stort (maks 10MB)', 'error');
        continue;
      }
      setUploading(true);
      try {
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const res = await axios.post(
          `${API_BASE}/api/training/sessions/${session.id}/upload-image`,
          { image_base64: base64 },
          { headers: authHeaders() }
        );
        if (res.data.success) {
          setImages((prev) => [...prev, res.data.image_url]);
          addToast('Bilde lastet opp', 'success');
        }
      } catch (err) {
        console.error('Upload error:', err);
        addToast('Kunne ikke laste opp bildet', 'error');
      } finally {
        setUploading(false);
      }
    }
  };

  const removeImage = (idx) => {
    if (window.confirm('Slett dette bildet?')) {
      setImages((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const handleClose = async () => {
    // Alltid synkron flush av diary før lukk (bypasser debounce)
    await flushDiarySaveNow();
    onUpdate?.();
    onClose();
  };

  // ---- ESC-tast lukker hovedmodalen (med autosave via handleClose) ----
  const handleCloseRef = useRef(handleClose);
  useEffect(() => {
    handleCloseRef.current = handleClose;
  });
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Ikke lukk hovedmodalen hvis Punch-Poeng eller Skreddersy er åpen
      if (punchSerieIdx !== null) return;
      if (showSkreddersy) return;
      handleCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, punchSerieIdx, showSkreddersy]);

  // Klikk utenfor hovedmodalen (på backdrop) → lagre og lukk
  const handleBackdropClick = (e) => {
    if (e.target !== e.currentTarget) return;
    if (punchSerieIdx !== null) return;
    if (showSkreddersy) return;
    handleClose();
  };

  // ---- Render helpers ----
  const enabledOrdered = useMemo(
    () => [...modules].sort((a, b) => a.order - b.order).filter((m) => m.enabled),
    [modules]
  );

  if (!isOpen) return null;

  // ---- ENKEL mode: render legacy simple diary modal ----
  if (mode === 'enkel') {
    return (
      <TrainingDiaryModal
        session={session}
        isOpen={isOpen}
        onClose={onClose}
        onUpdate={onUpdate}
        onSwitchMode={switchMode}
        mode={mode}
      />
    );
  }

  const today = new Date(session?.start_time || Date.now());
  const dateLabel = today
    .toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' })
    .toUpperCase();

  return createPortal(
    <>
    <div
      className="fixed overflow-y-auto"
      style={{
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999,
        background: 'rgba(17,24,39,0.55)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
      onClick={handleBackdropClick}
      data-testid="skytedagbok-modal"
    >
      <div
        className="mx-auto my-4 px-4 sm:px-6 max-w-2xl pb-8"
        style={{ color: '#FFFFFF' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between pt-4 mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-3xl font-extrabold tracking-tight">Skytedagbok</h2>
              <button
                type="button"
                onClick={() => switchMode('pro')}
                className="px-3 py-1 rounded-full text-[10px] font-extrabold tracking-widest uppercase"
                style={{
                  background: '#FFD700',
                  color: '#030712',
                }}
                data-testid="mode-pill-pro"
                title="PRO-modus (avansert)"
              >
                Pro
              </button>
              <button
                type="button"
                onClick={() => switchMode('enkel')}
                className="px-3 py-1 rounded-full text-[10px] font-extrabold tracking-widest uppercase"
                style={{
                  background: '#374151',
                  color: '#94A3B8',
                }}
                data-testid="mode-pill-enkel"
                title="Enkel modus (gammel skytedagbok)"
              >
                Enkel
              </button>
            </div>
            <p className="text-[10px] tracking-widest mt-1" style={{ color: '#94A3B8' }}>
              {dateLabel}
              {diarySaveState !== 'idle' && (
                <span
                  className="ml-3 font-bold"
                  style={{
                    color:
                      diarySaveState === 'saved'
                        ? '#10b981'
                        : diarySaveState === 'error'
                        ? '#ef4444'
                        : '#FFD700',
                  }}
                  data-testid="diary-autosave-indicator"
                >
                  {diarySaveState === 'saving' && '• Lagrer...'}
                  {diarySaveState === 'saved' && '• ✓ Lagret'}
                  {diarySaveState === 'error' && '• Feil'}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowSkreddersy(true)}
              className="text-[10px] font-extrabold tracking-widest px-3 py-2 rounded-md uppercase"
              style={{
                color: '#FFD700',
                border: '1px dashed #FFD700',
                background: 'transparent',
              }}
              data-testid="open-skreddersy-btn"
            >
              <Sliders className="inline w-3 h-3 mr-1" />
              Skreddersy
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 hover:opacity-80"
              style={{ color: '#FFFFFF' }}
              data-testid="close-skytedagbok-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modules in order */}
        <div className="space-y-4">
          {enabledOrdered.map((m) => (
            <Module
              key={m.id}
              id={m.id}
              // Pass everything via props
              state={{
                weapons, ammoList, customTags, customSights, trendData, trendPeriod,
                diaryWeaponId, diaryAmmoId, series,
                clicksHeightDir, clicksHeightCount,
                clicksSideDir, clicksSideCount,
                clicksHistory,
                persistedClicksHistory,                dagsform, windLevel, activeTags, activeSightTags,
                groupSizeMm, groupSizeCaliber, groupSizeShots, groupSizeNotes,
                showGroupInfo,
                timerSeconds, timerRunning, splitTimes,
                expandedSerieIdx,
                notes, images, uploading,
                addingWeapon, newWeaponName, newWeaponCal,
                editingWeaponId, editWeaponName, editWeaponCal,
                addingAmmo, newAmmoName, newAmmoBatch,
                editingAmmoId, editAmmoName, editAmmoBatch,
                addingTag, newTagName,
                editingTagId, editTagValue,
                addingSight, newSightName,
              }}
              actions={{
                setDiaryWeaponId, setDiaryAmmoId,
                updateSeries, addSeries, removeSeries,
                openPunchPoeng, uploadSerieImage, clearSerieImage, setSerieImageLightbox,
                uploadingSerieIdx,
                setClickHeight, setClickSide,
                setDagsform, setWindLevel, toggleTag, toggleSightTag,
                setGroupSizeMm, setGroupSizeCaliber, setGroupSizeShots, setGroupSizeNotes,
                setShowGroupInfo,
                startTimer, splitTimer, stopTimer, resetTimer,
                setExpandedSerieIdx,
                setNotes, handleImageUpload, removeImage,
                setAddingWeapon, setNewWeaponName, setNewWeaponCal, addWeapon, removeWeapon,
                importWeaponsFromProfile, importingWeapons,
                setEditWeaponName, setEditWeaponCal, startEditWeapon, cancelEditWeapon, saveEditWeapon,
                setAddingAmmo, setNewAmmoName, setNewAmmoBatch, addAmmo, removeAmmo,
                setEditAmmoName, setEditAmmoBatch, startEditAmmo, cancelEditAmmo, saveEditAmmo,
                setAddingTag, setNewTagName, addCustomTag,
                setEditTagValue, startEditTag, cancelEditTag, saveEditTag, removeCustomTag,
                setAddingSight, setNewSightName, addCustomSight, removeCustomSight,
                setTrendPeriod,
              }}
            />
          ))}
        </div>

        {/* End of modules. Auto-save handles persistence; sticky button below handles close. */}
      </div>

      {/* Sticky knapp er flyttet ut av scroll-container - se etter </div> nedenfor */}

      {/* Skreddersy overlay (rendret inne i scroll-container) */}
      {showSkreddersy && (
        <div
          className="fixed flex items-start justify-center overflow-y-auto"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
          data-testid="skreddersy-overlay"
        >
          <div
            className="mt-8 mb-24 w-full max-w-md mx-4 rounded-2xl shadow-2xl"
            style={{ background: '#0b1220', border: '1px solid #1f2937' }}
          >
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#1f2937' }}>
              <h3 className="text-xs font-extrabold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
                Skreddersy
              </h3>
              <div className="flex items-center gap-3">
                <span
                  className="text-[10px] font-bold tracking-widest uppercase"
                  style={{
                    color:
                      autoSaveState === 'saved'
                        ? '#10b981'
                        : autoSaveState === 'error'
                        ? '#ef4444'
                        : autoSaveState === 'saving'
                        ? '#FFD700'
                        : 'transparent',
                  }}
                  data-testid="autosave-indicator"
                >
                  {autoSaveState === 'saving' && 'Lagrer...'}
                  {autoSaveState === 'saved' && '✓ Lagret'}
                  {autoSaveState === 'error' && 'Feil'}
                </span>
                <button
                  type="button"
                  onClick={() => setShowSkreddersy(false)}
                  className="text-white hover:opacity-80"
                  data-testid="close-skreddersy-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-2">
              {[...modules].sort((a, b) => a.order - b.order).map((m, i, arr) => (
                <div
                  key={m.id}
                  data-skreddersy-row={m.id}
                  draggable
                  onDragStart={() => handleDragStart(m.id)}
                  onDragOver={(e) => handleDragOver(e, m.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={() => handleTouchStart(m.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className={`flex items-center gap-3 px-3 py-3 rounded-md cursor-move ${
                    dragId === m.id ? 'opacity-50 bg-gray-700' : ''
                  } hover:bg-gray-800`}
                  style={{ touchAction: 'none' }}
                  data-testid={`skreddersy-row-${m.id}`}
                >
                  <GripVertical className="w-5 h-5" style={{ color: '#94A3B8' }} />
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => moveModule(m.id, 'up')}
                      disabled={i === 0}
                      className="p-1.5 rounded disabled:opacity-30 hover:bg-gray-700"
                      data-testid={`move-up-${m.id}`}
                    >
                      <ArrowUp className="w-4 h-4" style={{ color: '#FFD700' }} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveModule(m.id, 'down')}
                      disabled={i === arr.length - 1}
                      className="p-1.5 rounded disabled:opacity-30 hover:bg-gray-700"
                      data-testid={`move-down-${m.id}`}
                    >
                      <ArrowDown className="w-4 h-4" style={{ color: '#FFD700' }} />
                    </button>
                  </div>
                  <span className="flex-1 text-base" style={{ color: '#FFFFFF' }}>
                    {MODULE_LABELS[m.id]}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleModule(m.id)}
                    role="switch"
                    aria-checked={m.enabled}
                    className="relative w-12 h-6 rounded-full transition"
                    style={{ background: m.enabled ? '#FFD700' : '#374151' }}
                    data-testid={`toggle-${m.id}`}
                  >
                    <span
                      className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                      style={{ left: m.enabled ? '26px' : '2px' }}
                    />
                  </button>
                </div>
              ))}
            </div>
            <div className="p-4 border-t text-center" style={{ borderColor: '#1f2937' }}>
              <button
                type="button"
                onClick={() => setShowSkreddersy(false)}
                className="w-full py-3 rounded-xl font-bold uppercase tracking-wider text-sm"
                style={{ background: '#FFD700', color: '#030712' }}
                data-testid="close-skreddersy-bottom-btn"
              >
                Ferdig
              </button>
              <p className="text-[9px] mt-2 tracking-wider" style={{ color: '#94A3B8' }}>
                Endringer lagres automatisk
              </p>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Punch Poeng-modal: aktiv når skyteleder/medlem klikker Target-knappen ved siden av serie-poeng */}
    {punchSerieIdx !== null && (
      <PunchPoengModal
        serieIdx={punchSerieIdx}
        initialHits={series[punchSerieIdx]?.hits || []}
        onCommit={commitPunchPoeng}
      />
    )}
    {/* Lightbox for blinkbilde-preview */}
    {serieImageLightbox && (
      <div
        className="fixed inset-0 z-[200001] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.9)' }}
        onClick={() => setSerieImageLightbox(null)}
        data-testid="serie-image-lightbox"
      >
        <button
          type="button"
          onClick={() => setSerieImageLightbox(null)}
          className="absolute top-4 right-4 p-2 rounded-full bg-black/60 hover:bg-black text-white"
          data-testid="serie-image-lightbox-close"
          aria-label="Lukk"
        >
          <X className="w-6 h-6" />
        </button>
        <img
          src={serieImageLightbox}
          alt="Blinkbilde"
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>,
    document.body
  );
};

// ---- Punch Poeng Modal (treff-for-treff poenginntasting) ----
// Lagrer SYNKRON til backend før lukking. Ingen debounce. FERDIG og X kaller
// commitPunchPoeng direkte via onCommit. Parent sin commitPunchPoeng utfører
// setSeries + synkron PUT /api/training/sessions/{id}/diary før den lukker modalen.
const PunchPoengModal = ({ serieIdx, initialHits, onCommit }) => {
  const [hits, setHits] = useState(Array.isArray(initialHits) ? [...initialHits] : []);
  const [saving, setSaving] = useState(false);
  const total = hits.reduce((sum, h) => sum + (h === 'X' ? 10 : Number(h) || 0), 0);
  const xCount = hits.filter((h) => h === 'X').length;

  const handleCommit = async (e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await onCommit(serieIdx, hits);
    } finally {
      setSaving(false);
    }
  };

  // ESC lagrer synkront og lukker
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleCommit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [hits, saving]);

  const addHit = (v) => setHits((prev) => [...prev, v]);
  const removeHit = (i) => setHits((prev) => prev.filter((_, idx) => idx !== i));
  const reset = () => setHits([]);
  const keys = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 'X'];

  return createPortal(
    <div
      className="fixed inset-0 z-[200000] flex items-start justify-center p-4 overflow-auto"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={handleCommit}
      data-testid="punch-poeng-overlay"
    >
      <div
        className="w-full max-w-md mt-16 rounded-2xl p-5"
        style={{ background: '#111827', border: '1px solid #1f2937', position: 'relative' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="punch-poeng-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs font-extrabold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
              Punch Poeng – Serie {serieIdx + 1}
            </div>
            <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>Klikk hvert treff. X = innertier.</p>
          </div>
          <button
            type="button"
            onClick={handleCommit}
            onTouchEnd={(e) => { e.preventDefault(); handleCommit(e); }}
            disabled={saving}
            className="p-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
            style={{ color: '#94A3B8' }}
            data-testid="punch-poeng-close"
            aria-label="Lagre og lukk"
          >
            <X className="w-5 h-5 pointer-events-none" />
          </button>
        </div>
        <div className="flex items-end justify-between py-2">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold" style={{ color: '#FFD700' }}>{total}</span>
            <span className="text-sm" style={{ color: '#94A3B8' }}>poeng</span>
          </div>
          <div className="text-xs text-right" style={{ color: '#94A3B8' }}>
            Skudd: <span className="text-white font-bold">{hits.length}</span>
            <span className="ml-3">X (Inner): <span className="text-white font-bold">{xCount}</span></span>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-[10px] font-extrabold tracking-widest uppercase mb-2" style={{ color: '#94A3B8' }}>Treff</div>
          <div className="min-h-[44px] p-2 rounded-md" style={{ background: '#0b1220' }}>
            {hits.length === 0 ? (
              <p className="text-xs italic" style={{ color: '#64748b' }}>Ingen treff registrert ennå – klikk en knapp under for å starte.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {hits.map((h, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-bold"
                    style={{ background: '#1f2937', color: h === 'X' ? '#FFD700' : '#fff' }}
                    data-testid={`punch-hit-pill-${i}`}
                  >
                    {h}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeHit(i); }}
                      className="opacity-60 hover:opacity-100"
                      aria-label="Fjern treff"
                    >
                      <X className="w-3 h-3 pointer-events-none" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4">
          {keys.map((k) => {
            const isHighlight = k === 10 || k === 'X';
            return (
              <button
                key={k}
                type="button"
                onClick={(e) => { e.stopPropagation(); addHit(k); }}
                className="py-4 rounded-lg text-xl font-bold transition active:scale-95"
                style={{
                  background: isHighlight ? (k === 'X' ? '#FFD700' : 'rgba(255,215,0,0.15)') : '#1f2937',
                  color: isHighlight && k === 'X' ? '#030712' : '#fff',
                  border: isHighlight && k === 10 ? '1px solid #FFD700' : '1px solid #374151',
                }}
                data-testid={`punch-key-${k}`}
              >
                {k}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-2 mt-4">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); reset(); }}
            className="px-5 py-3 rounded-lg font-bold uppercase tracking-wider text-xs"
            style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444' }}
            data-testid="punch-reset"
          >
            Nullstill
          </button>
          <button
            type="button"
            onClick={handleCommit}
            onTouchEnd={(e) => { e.preventDefault(); handleCommit(e); }}
            disabled={saving}
            className="px-6 py-3 rounded-lg font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-70"
            style={{ background: '#FFD700', color: '#030712' }}
            data-testid="punch-ferdig"
          >
            <Check className="w-4 h-4 pointer-events-none" />
            <span className="pointer-events-none">{saving ? 'Lagrer...' : 'Ferdig'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---- Module renderer ----
const Card = ({ children, label }) => (
  <div
    className="rounded-2xl p-4"
    style={{ background: '#111827', border: '1px solid #1f2937' }}
  >
    {label && (
      <div
        className="text-[10px] font-extrabold tracking-widest uppercase mb-3"
        style={{ color: '#94A3B8' }}
      >
        {label}
      </div>
    )}
    {children}
  </div>
);

const Module = ({ id, state, actions }) => {
  switch (id) {
    case 'vapengarderobe':
      return <WeaponModule state={state} actions={actions} />;
    case 'sikte_type':
      return <SightModule state={state} actions={actions} />;
    case 'ammunisjons_logg':
      return <AmmoModule state={state} actions={actions} />;
    case 'serie_split':
      return <SeriesModule state={state} actions={actions} />;
    case 'klikktelling':
      return <ClickModule state={state} actions={actions} />;
    case 'gruppestorrelse':
      return <GroupSizeModule state={state} actions={actions} />;
    case 'dagsform':
      return <DagsformModule state={state} actions={actions} />;
    case 'hurtig_tagger':
      return <TagsModule state={state} actions={actions} />;
    case 'tidsbruk_timer':
      return <TimerModule state={state} actions={actions} />;
    case 'progresjonsgraf':
      return <TrendModule state={state} actions={actions} />;
    case 'bildeopplasting':
      return <ImagesModule state={state} actions={actions} />;
    case 'personlige_notater':
      return <NotesModule state={state} actions={actions} />;
    default:
      return null;
  }
};

const SightModule = ({ state, actions }) => {
  const customSightsByName = new Map((state.customSights || []).map((t) => [t.name, t]));
  const allSights = [
    ...DEFAULT_SIGHT_TAGS.map((name) => ({ name, isDefault: true, id: `default-sight-${name}` })),
    ...(state.customSights || [])
      .filter((t) => !DEFAULT_SIGHT_TAGS.includes(t.name))
      .map((t) => ({ ...t, isDefault: false })),
  ];
  return (
    <Card label="Sikte type">
      <div className="flex flex-wrap gap-2 mb-3">
        {allSights.map((t) => {
          const active = (state.activeSightTags || []).includes(t.name);
          return (
            <div key={t.id} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => actions.toggleSightTag(t.name)}
                className={`px-4 py-1.5 text-xs font-bold transition ${
                  !t.isDefault ? 'rounded-l-full' : 'rounded-full'
                }`}
                style={{
                  background: active ? '#FFD700' : '#1f2937',
                  color: active ? '#030712' : '#94A3B8',
                }}
                data-testid={`sight-tag-${t.name}`}
              >
                {t.name}
              </button>
              {!t.isDefault && (
                <div
                  className="flex items-center rounded-r-full px-1.5"
                  style={{ background: active ? '#FFD700' : '#1f2937' }}
                >
                  <button
                    type="button"
                    onClick={() => actions.removeCustomSight(customSightsByName.get(t.name))}
                    className="p-1 hover:bg-black/10 rounded-full"
                    title="Slett"
                    data-testid={`delete-sight-${t.name}`}
                  >
                    <Trash2
                      className="w-3 h-3"
                      style={{ color: active ? '#7f1d1d' : '#ef4444' }}
                    />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {state.addingSight ? (
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Sikte-navn"
            value={state.newSightName}
            onChange={(e) => actions.setNewSightName(e.target.value)}
            className="flex-1 bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
            data-testid="new-sight-name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') actions.addCustomSight();
            }}
          />
          <button
            type="button"
            onClick={actions.addCustomSight}
            className="px-4 py-2 rounded-md font-bold text-xs uppercase"
            style={{ background: '#FFD700', color: '#030712' }}
            data-testid="confirm-add-sight"
          >
            Legg til
          </button>
          <button
            type="button"
            onClick={() => actions.setAddingSight(false)}
            className="px-3 py-2 rounded-md text-xs uppercase"
            style={{ background: '#374151', color: '#fff' }}
          >
            Avbryt
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => actions.setAddingSight(true)}
          className="w-full py-2 text-[10px] font-extrabold tracking-widest uppercase rounded-md"
          style={{ color: '#FFD700', border: '1px dashed #FFD700', background: 'transparent' }}
          data-testid="add-sight-btn"
        >
          + Legg til egen sikte-type
        </button>
      )}
    </Card>
  );
};

const GroupSizeModule = ({ state, actions }) => {
  // CTC calc
  const mm = state.groupSizeMm === '' || state.groupSizeMm === null || state.groupSizeMm === undefined
    ? null
    : (Number.isFinite(Number(state.groupSizeMm)) ? Number(state.groupSizeMm) : null);
  const cal = state.groupSizeCaliber === '' || state.groupSizeCaliber === null || state.groupSizeCaliber === undefined
    ? null
    : (Number.isFinite(Number(state.groupSizeCaliber)) ? Number(state.groupSizeCaliber) : null);
  const ctc = mm !== null && cal !== null ? Math.max(0, mm - cal).toFixed(1) : null;
  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-extrabold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
            Gruppestørrelse
          </div>
          <button
            type="button"
            onClick={() => actions.setShowGroupInfo(true)}
            className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-700"
            title="Hva er gruppestørrelse?"
            data-testid="group-info-btn"
            style={{ border: '1px solid #94A3B8' }}
          >
            <span className="text-xs font-bold" style={{ color: '#94A3B8' }}>i</span>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[9px] font-bold tracking-widest uppercase mb-1" style={{ color: '#94A3B8' }}>
              Ytter-til-ytter (mm)
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={state.groupSizeMm}
              onChange={(e) => actions.setGroupSizeMm(e.target.value)}
              placeholder="22.0"
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              data-testid="group-size-mm"
            />
          </div>
          <div>
            <label className="block text-[9px] font-bold tracking-widest uppercase mb-1" style={{ color: '#94A3B8' }}>
              Kaliber (mm)
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={state.groupSizeCaliber}
              onChange={(e) => actions.setGroupSizeCaliber(e.target.value)}
              placeholder="6.5"
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              data-testid="group-size-caliber"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[9px] font-bold tracking-widest uppercase mb-1" style={{ color: '#94A3B8' }}>
              Antall skudd
            </label>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              value={state.groupSizeShots}
              onChange={(e) => actions.setGroupSizeShots(e.target.value)}
              placeholder="5"
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              data-testid="group-size-shots"
            />
          </div>
          <div>
            <label className="block text-[9px] font-bold tracking-widest uppercase mb-1" style={{ color: '#94A3B8' }}>
              C-T-C (mm)
            </label>
            <div
              className="w-full rounded-md px-3 py-2 text-lg font-extrabold tabular-nums"
              style={{ background: '#0b1220', color: '#FFD700', border: '1px solid #1f2937' }}
              data-testid="group-size-ctc"
            >
              {ctc !== null ? `${ctc} mm` : '—'}
            </div>
          </div>
        </div>
        <textarea
          rows={2}
          placeholder="Notater (vær, rifle, ammo...)"
          value={state.groupSizeNotes}
          onChange={(e) => actions.setGroupSizeNotes(e.target.value)}
          className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-xs text-white placeholder-gray-500 resize-none"
          style={{ border: '1px solid #374151' }}
          data-testid="group-size-notes"
        />
      </Card>
      {state.showGroupInfo && createPortal((
        <div
          className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => actions.setShowGroupInfo(false)}
          data-testid="group-info-modal"
        >
          <div
            className="max-w-lg w-full rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
            style={{ background: '#0b1220', border: '1px solid #1f2937' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-extrabold" style={{ color: '#FFD700' }}>
                Gruppestørrelse
              </h3>
              <button
                type="button"
                onClick={() => actions.setShowGroupInfo(false)}
                className="text-white"
                data-testid="close-group-info"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-sm space-y-3" style={{ color: '#cbd5e1' }}>
              <p>
                Loggfør hvor nøyaktig du og rifla di skyter ved å måle avstanden
                mellom kulehullene. I stedet for "det var en bra serie" får du
                kalde, harde fakta i millimeter som kan analyseres over tid.
              </p>
              <div>
                <p className="font-bold text-white mb-1">Hva betyr "mm-spredning"?</p>
                <p>
                  Senter-til-senter (C-T-C): avstanden fra midten av det
                  kulehullet som er lengst til venstre, til midten av det som er
                  lengst til høyre i en serie (f.eks. 5 skudd).
                </p>
              </div>
              <div>
                <p className="font-bold text-white mb-1">Hvorfor manuelt?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    <strong>Presisjon over tid:</strong> se om du blir bedre,
                    eller om utstyret presterer dårligere (f.eks. utslitt løp).
                  </li>
                  <li>
                    <strong>Ammunisjonstesting:</strong> objektivt sammenligne
                    hvilken patron rifla di liker best.
                  </li>
                  <li>
                    <strong>Vær og vind:</strong> korreler små grupper med
                    forholdene. Kanskje skyter du 12 mm i vindstille men 30 mm
                    når det blåser.
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-white mb-1">Slik regner det ut</p>
                <p>Mål med skyvelære fra ytterkant til ytterkant av de to hullene som er lengst fra hverandre, og trekk fra kulas diameter:</p>
                <p className="my-2 px-3 py-2 rounded-md text-center" style={{ background: '#030712', color: '#FFD700' }}>
                  <span className="font-extrabold tabular-nums">22 mm − 6,5 mm = 15,5 mm</span>
                </p>
                <p>
                  Tallet du sitter igjen med er din faktiske gruppestørrelse i
                  millimeter.
                </p>
              </div>
              <p className="italic" style={{ color: '#94A3B8' }}>
                Ved å logge dette konsekvent kan du skille mellom flaks og
                ferdighet. Snittet ditt over 10 turer på banen forteller deg
                nøyaktig hva du og rifla er gode for.
              </p>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
};

const WeaponModule = ({ state, actions }) => (
  <Card label={
    <div className="flex items-center justify-between w-full">
      <span>Våpengarderobe</span>
      <button
        type="button"
        onClick={actions.importWeaponsFromProfile}
        disabled={state.importingWeapons}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-extrabold tracking-widest uppercase disabled:opacity-50"
        style={{
          color: '#FFD700',
          border: '1px solid #FFD700',
          background: 'transparent',
        }}
        title="Importer våpen fra Min profil"
        data-testid="import-weapons-btn"
      >
        <Download className="w-3 h-3" />
        {state.importingWeapons ? 'Henter...' : 'Hent fra profil'}
      </button>
    </div>
  }>
    {state.weapons.length === 0 && !state.addingWeapon && (
      <div className="text-sm mb-3" style={{ color: '#94A3B8' }}>
        Ingen våpen lagret ennå.
      </div>
    )}
    {state.weapons.map((w, idx) => {
      const isEditing = state.editingWeaponId === w.id;
      if (isEditing) {
        return (
          <div
            key={w.id}
            className={`py-3 ${idx > 0 ? 'border-t' : ''} space-y-2`}
            style={{ borderColor: '#1f2937' }}
          >
            <input
              type="text"
              value={state.editWeaponName}
              onChange={(e) => actions.setEditWeaponName(e.target.value)}
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              placeholder="Navn"
              data-testid={`edit-weapon-name-${w.id}`}
              autoFocus
            />
            <input
              type="text"
              value={state.editWeaponCal}
              onChange={(e) => actions.setEditWeaponCal(e.target.value)}
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              placeholder="Kaliber"
              data-testid={`edit-weapon-cal-${w.id}`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={actions.saveEditWeapon}
                className="flex-1 py-2 rounded-md font-bold text-xs uppercase"
                style={{ background: '#FFD700', color: '#030712' }}
                data-testid={`save-edit-weapon-${w.id}`}
              >
                Lagre
              </button>
              <button
                type="button"
                onClick={actions.cancelEditWeapon}
                className="flex-1 py-2 rounded-md text-xs uppercase"
                style={{ background: '#374151', color: '#fff' }}
              >
                Avbryt
              </button>
            </div>
          </div>
        );
      }
      return (
        <div
          key={w.id}
          className={`flex items-center justify-between py-2 ${idx > 0 ? 'border-t' : ''}`}
          style={{ borderColor: '#1f2937' }}
        >
          <button
            type="button"
            onClick={() => actions.setDiaryWeaponId(w.id)}
            className="flex-1 text-left"
            data-testid={`select-weapon-${w.id}`}
          >
            <div className="font-bold text-base">{w.name}</div>
            {w.caliber && (
              <div className="text-xs" style={{ color: '#94A3B8' }}>
                {w.caliber}
              </div>
            )}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => actions.startEditWeapon(w)}
              className="opacity-60 hover:opacity-100"
              title="Rediger"
              data-testid={`edit-weapon-${w.id}`}
            >
              <Pencil className="w-4 h-4" style={{ color: '#94A3B8' }} />
            </button>
            <button
              type="button"
              onClick={() => actions.removeWeapon(w.id)}
              className="opacity-50 hover:opacity-100"
              data-testid={`delete-weapon-${w.id}`}
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
            <CheckCircle2
              className="w-5 h-5"
              style={{
                color: state.diaryWeaponId === w.id ? '#FFD700' : '#374151',
              }}
            />
          </div>
        </div>
      );
    })}
    {state.addingWeapon ? (
      <div className="mt-3 space-y-2">
        <input
          type="text"
          placeholder="Navn (f.eks. Pardini SP)"
          value={state.newWeaponName}
          onChange={(e) => actions.setNewWeaponName(e.target.value)}
          className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
          data-testid="new-weapon-name"
        />
        <input
          type="text"
          placeholder="Kaliber (f.eks. .22 cal)"
          value={state.newWeaponCal}
          onChange={(e) => actions.setNewWeaponCal(e.target.value)}
          className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
          data-testid="new-weapon-cal"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={actions.addWeapon}
            className="flex-1 py-2 rounded-md font-bold text-xs uppercase"
            style={{ background: '#FFD700', color: '#030712' }}
            data-testid="confirm-add-weapon"
          >
            Legg til
          </button>
          <button
            type="button"
            onClick={() => actions.setAddingWeapon(false)}
            className="flex-1 py-2 rounded-md text-xs uppercase"
            style={{ background: '#374151', color: '#fff' }}
          >
            Avbryt
          </button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => actions.setAddingWeapon(true)}
        className="mt-2 text-[10px] font-extrabold tracking-widest"
        style={{ color: '#FFD700' }}
        data-testid="add-weapon-btn"
      >
        + LEGG TIL NYTT VÅPEN
      </button>
    )}
  </Card>
);

const AmmoModule = ({ state, actions }) => (
  <Card label="Ammunisjons-logg">
    {state.ammoList.length === 0 && !state.addingAmmo && (
      <div className="text-sm mb-3" style={{ color: '#94A3B8' }}>
        Ingen ammunisjon lagret ennå.
      </div>
    )}
    {state.ammoList.map((a, idx) => {
      const isEditing = state.editingAmmoId === a.id;
      if (isEditing) {
        return (
          <div
            key={a.id}
            className={`py-3 ${idx > 0 ? 'border-t' : ''} space-y-2`}
            style={{ borderColor: '#1f2937' }}
          >
            <input
              type="text"
              value={state.editAmmoName}
              onChange={(e) => actions.setEditAmmoName(e.target.value)}
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              placeholder="Navn"
              data-testid={`edit-ammo-name-${a.id}`}
              autoFocus
            />
            <input
              type="text"
              value={state.editAmmoBatch}
              onChange={(e) => actions.setEditAmmoBatch(e.target.value)}
              className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
              placeholder="Batch-nummer"
              data-testid={`edit-ammo-batch-${a.id}`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={actions.saveEditAmmo}
                className="flex-1 py-2 rounded-md font-bold text-xs uppercase"
                style={{ background: '#FFD700', color: '#030712' }}
                data-testid={`save-edit-ammo-${a.id}`}
              >
                Lagre
              </button>
              <button
                type="button"
                onClick={actions.cancelEditAmmo}
                className="flex-1 py-2 rounded-md text-xs uppercase"
                style={{ background: '#374151', color: '#fff' }}
              >
                Avbryt
              </button>
            </div>
          </div>
        );
      }
      return (
        <div
          key={a.id}
          className={`flex items-center justify-between py-2 ${idx > 0 ? 'border-t' : ''}`}
          style={{ borderColor: '#1f2937' }}
        >
          <button
            type="button"
            onClick={() => actions.setDiaryAmmoId(a.id)}
            className="flex-1 text-left"
            data-testid={`select-ammo-${a.id}`}
          >
            <div className="font-bold text-base">
              {a.name}
              {a.batch_number ? ` (#${a.batch_number})` : ''}
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => actions.startEditAmmo(a)}
              className="opacity-60 hover:opacity-100"
              title="Rediger"
              data-testid={`edit-ammo-${a.id}`}
            >
              <Pencil className="w-4 h-4" style={{ color: '#94A3B8' }} />
            </button>
            <button
              type="button"
              onClick={() => actions.removeAmmo(a.id)}
              className="opacity-50 hover:opacity-100"
              data-testid={`delete-ammo-${a.id}`}
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
            <CheckCircle2
              className="w-5 h-5"
              style={{
                color: state.diaryAmmoId === a.id ? '#FFD700' : '#374151',
              }}
            />
          </div>
        </div>
      );
    })}
    {state.addingAmmo ? (
      <div className="mt-3 space-y-2">
        <input
          type="text"
          placeholder="Navn (f.eks. SK Long Range)"
          value={state.newAmmoName}
          onChange={(e) => actions.setNewAmmoName(e.target.value)}
          className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
          data-testid="new-ammo-name"
        />
        <input
          type="text"
          placeholder="Batch-nummer (f.eks. 451)"
          value={state.newAmmoBatch}
          onChange={(e) => actions.setNewAmmoBatch(e.target.value)}
          className="w-full bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
          data-testid="new-ammo-batch"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={actions.addAmmo}
            className="flex-1 py-2 rounded-md font-bold text-xs uppercase"
            style={{ background: '#FFD700', color: '#030712' }}
            data-testid="confirm-add-ammo"
          >
            Legg til
          </button>
          <button
            type="button"
            onClick={() => actions.setAddingAmmo(false)}
            className="flex-1 py-2 rounded-md text-xs uppercase"
            style={{ background: '#374151', color: '#fff' }}
          >
            Avbryt
          </button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => actions.setAddingAmmo(true)}
        className="mt-2 text-[10px] font-extrabold tracking-widest"
        style={{ color: '#FFD700' }}
        data-testid="add-ammo-btn"
      >
        + LEGG TIL NY AMMUNISJON
      </button>
    )}
  </Card>
);

const SeriesModule = ({ state, actions }) => (
  <Card>
    <div className="text-[10px] font-extrabold tracking-widest uppercase mb-3" style={{ color: '#94A3B8' }}>
      Serie-split & X
    </div>
    {/* Column headers aligned to the input columns */}
    <div className="flex items-end justify-end gap-2 mb-1 text-[8px] font-bold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
      <span className="w-16 text-center">Poeng</span>
      <span className="w-16 text-center">Inner</span>
      <span className="w-4" />
    </div>
    {state.series.map((s, idx) => {
      const expanded = state.expandedSerieIdx === idx;
      const splits = Array.isArray(s.splits) ? s.splits : [];
      const hasDetails = s.time > 0 || splits.length > 0;
      return (
        <div key={idx} className="mb-2">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className={`flex-1 min-w-0 text-left ${hasDetails ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
              onClick={() =>
                hasDetails &&
                actions.setExpandedSerieIdx(expanded ? null : idx)
              }
              data-testid={`series-row-${idx}`}
            >
              <div className="font-semibold flex items-center gap-2">
                Serie {idx + 1}
                {hasDetails && (
                  <span className="text-[10px]" style={{ color: '#94A3B8' }}>
                    {expanded ? '▾' : '▸'}
                  </span>
                )}
              </div>
              {s.time > 0 && (
                <div className="text-xs tabular-nums mt-0.5" style={{ color: '#FFD700' }}>
                  ⏱ {formatTime(s.time)}
                  {splits.length > 0 && (
                    <span className="ml-2" style={{ color: '#94A3B8' }}>
                      ({splits.length} splitt{splits.length > 1 ? 'er' : ''})
                    </span>
                  )}
                </div>
              )}
            </button>
            <div className="flex gap-2 items-center">
              {s.target_image ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => actions.setSerieImageLightbox(s.target_image)}
                    className="w-9 h-9 rounded-md overflow-hidden border border-[#FFD700] relative group"
                    title="Vis blinkbilde"
                    data-testid={`series-image-preview-${idx}`}
                  >
                    <img src={s.target_image} alt="Blink" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
                      <Eye className="w-4 h-4 text-white" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.clearSerieImage(idx)}
                    className="w-5 h-5 rounded-full flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 border border-red-500/40"
                    title="Fjern blinkbilde"
                    data-testid={`series-image-remove-${idx}`}
                  >
                    <X className="w-2.5 h-2.5 text-red-300" />
                  </button>
                </div>
              ) : (
                <label
                  className="w-9 h-9 rounded-md flex items-center justify-center bg-[#1f2937] border border-[#374151] hover:border-[#FFD700] hover:bg-[#283140] transition cursor-pointer relative"
                  title="Last opp blinkbilde (foto som dokumentasjon)"
                  data-testid={`series-image-upload-${idx}`}
                >
                  {actions.uploadingSerieIdx === idx ? (
                    <div className="w-3 h-3 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" style={{ color: '#FFD700' }} />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) actions.uploadSerieImage(idx, f);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
              <input
                type="number"
                inputMode="none"
                readOnly
                min="0"
                value={s.poeng}
                onClick={() => actions.openPunchPoeng(idx)}
                className="w-16 text-center bg-[#1f2937] rounded-md py-2 font-bold text-white cursor-pointer hover:bg-[#283140]"
                data-testid={`series-poeng-${idx}`}
                placeholder="0"
                title="Klikk for å punche poeng"
              />
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={s.inner}
                onChange={(e) => actions.updateSeries(idx, 'inner', e.target.value)}
                className="w-16 text-center bg-[#1f2937] rounded-md py-2 font-bold text-white"
                data-testid={`series-inner-${idx}`}
              />
              <div className="flex flex-col w-4 items-center">
                <button
                  type="button"
                  onClick={() => actions.addSeries()}
                  className="px-1"
                  style={{ color: '#FFD700' }}
                  data-testid={`series-add-${idx}`}
                >
                  <Plus className="w-3 h-3" />
                </button>
                {state.series.length > 1 && (
                  <button
                    type="button"
                    onClick={() => actions.removeSeries(idx)}
                    className="px-1 opacity-60 hover:opacity-100"
                    data-testid={`series-remove-${idx}`}
                  >
                    <X className="w-3 h-3 text-red-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
          {expanded && hasDetails && (
            <div
              className="mt-2 ml-2 pl-3 py-2 rounded-md text-xs space-y-1"
              style={{ background: '#0b1220', borderLeft: '2px solid #FFD700' }}
              data-testid={`series-details-${idx}`}
            >
              {splits.length === 0 ? (
                <div style={{ color: '#94A3B8' }}>Ingen splittider</div>
              ) : (
                <>
                  <div className="text-[9px] font-extrabold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
                    Splittider
                  </div>
                  {splits.map((sp, i) => (
                    <div key={i} className="tabular-nums flex justify-between" style={{ color: '#FFD700' }}>
                      <span>Splitt #{i + 1}</span>
                      <span>{formatTime(sp)}</span>
                    </div>
                  ))}
                  {s.time > 0 && (
                    <div className="tabular-nums flex justify-between border-t pt-1 mt-1" style={{ color: '#fff', borderColor: '#1f2937' }}>
                      <span>Total</span>
                      <span>{formatTime(s.time)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      );
    })}
    <button
      type="button"
      onClick={actions.addSeries}
      className="w-full mt-2 py-2 text-[10px] font-extrabold tracking-widest uppercase rounded-md"
      style={{
        color: '#94A3B8',
        border: '1px dashed #374151',
        background: 'transparent',
      }}
      data-testid="add-series-btn"
    >
      + Neste serie
    </button>
  </Card>
);

const ClickModule = ({ state, actions }) => {
  const Btn = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-10 h-10 flex items-center justify-center rounded-md"
      style={{
        background: active ? '#FFD700' : '#1f2937',
        color: active ? '#030712' : '#94A3B8',
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
  return (
    <Card label="Sikte klikktelling">
      <div className="flex flex-wrap gap-x-10 gap-y-4 items-center">
        <div className="flex items-center gap-3">
          <span
            className="text-sm font-extrabold tracking-widest uppercase"
            style={{ color: '#94A3B8' }}
          >
            Høyde
          </span>
          <Btn
            active={state.clicksHeightDir === 'up' && state.clicksHeightCount > 0}
            onClick={() => actions.setClickHeight('up')}
            testid="click-height-up"
          >
            <ChevronUp className="w-5 h-5" />
          </Btn>
          <Btn
            active={state.clicksHeightDir === 'down' && state.clicksHeightCount > 0}
            onClick={() => actions.setClickHeight('down')}
            testid="click-height-down"
          >
            <ChevronDown className="w-5 h-5" />
          </Btn>
          <span className="text-2xl font-extrabold ml-1" style={{ color: '#FFD700' }}>
            {state.clicksHeightCount}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="text-sm font-extrabold tracking-widest uppercase"
            style={{ color: '#94A3B8' }}
          >
            Side
          </span>
          <Btn
            active={state.clicksSideDir === 'left' && state.clicksSideCount > 0}
            onClick={() => actions.setClickSide('left')}
            testid="click-side-left"
          >
            <ChevronLeft className="w-5 h-5" />
          </Btn>
          <Btn
            active={state.clicksSideDir === 'right' && state.clicksSideCount > 0}
            onClick={() => actions.setClickSide('right')}
            testid="click-side-right"
          >
            <ChevronRight className="w-5 h-5" />
          </Btn>
          <span className="text-2xl font-extrabold ml-1" style={{ color: '#FFD700' }}>
            {state.clicksSideCount}
          </span>
        </div>
      </div>
      {/* Justeringshistorikk – merger nåværende + tidligere økter fra alle skytedagbøker */}
      {(() => {
        const combined = [
          ...(state.clicksHistory || []),
          ...(state.persistedClicksHistory || []),
        ];
        // De-dup on `at` (ISO timestamp) so persisted entries don't clash with current session
        const seen = new Set();
        const merged = combined.filter((h) => {
          const key = h && h.at ? h.at : Math.random().toString();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (merged.length === 0) return null;
        return <ClicksHistoryPaginated history={merged} />;
      })()}
    </Card>
  );
};

const ClicksHistoryPaginated = ({ history }) => {
  const INITIAL_VISIBLE = 6;
  const PAGE_SIZE = 6;
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  // Collapsed: just show the newest INITIAL_VISIBLE entries.
  // Expanded: paginated view (PAGE_SIZE per page).
  const slice = expanded
    ? history.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)
    : history.slice(0, INITIAL_VISIBLE);
  const start = expanded ? safePage * PAGE_SIZE : 0;
  const canExpand = history.length > INITIAL_VISIBLE;

  return (
    <div className="mt-4 pt-3 border-t" style={{ borderColor: '#1f2937' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-extrabold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
          Historikk
          <span className="ml-2 font-bold" style={{ color: '#FFD700' }}>
            ({history.length})
          </span>
        </div>
        {expanded && totalPages > 1 && (
          <div className="flex items-center gap-1.5 text-[10px]" style={{ color: '#94A3B8' }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="w-6 h-6 rounded flex items-center justify-center disabled:opacity-30 hover:bg-gray-700"
              data-testid="clicks-history-prev"
              style={{ border: '1px solid #374151' }}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className="font-bold tabular-nums">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="w-6 h-6 rounded flex items-center justify-center disabled:opacity-30 hover:bg-gray-700"
              data-testid="clicks-history-next"
              style={{ border: '1px solid #374151' }}
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      <div className="space-y-1.5" data-testid="clicks-history">
        {slice.map((h, i) => {
          const d = new Date(h.at);
          const day = d.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' });
          const tm = d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
          return (
            <div
              key={start + i}
              className="flex justify-between text-xs"
              data-testid={`clicks-history-${start + i}`}
            >
              <span style={{ color: '#94A3B8' }}>
                {day} {tm}
              </span>
              <span style={{ color: '#FFD700' }} className="font-semibold">
                {h.summary}
              </span>
            </div>
          );
        })}
      </div>
      {canExpand && (
        <button
          type="button"
          onClick={() => { setExpanded((e) => !e); setPage(0); }}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1.5 rounded hover:bg-gray-700/40 transition"
          style={{ color: '#FFD700', border: '1px solid #374151' }}
          data-testid="clicks-history-toggle-expand"
        >
          {expanded ? (
            <>Vis mindre <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>Les mer ({history.length - INITIAL_VISIBLE} eldre) <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
};

const _ClickModuleEnd = () => null; // marker

const DagsformModule = ({ state, actions }) => {
  const moods = ['😞', '😐', '🙂', '😀', '🤩'];
  return (
    <Card label="Dagsform">
      <div className="flex justify-between mb-4">
        {moods.map((emo, i) => (
          <button
            key={i}
            type="button"
            onClick={() => actions.setDagsform(i + 1)}
            className="text-3xl transition-transform"
            style={{
              filter: state.dagsform === i + 1 ? 'none' : 'grayscale(60%) opacity(0.45)',
              transform: state.dagsform === i + 1 ? 'scale(1.2)' : 'scale(1)',
            }}
            data-testid={`dagsform-${i + 1}`}
          >
            {emo}
          </button>
        ))}
      </div>
      <div className="text-[10px] font-extrabold tracking-widest uppercase mb-2" style={{ color: '#94A3B8' }}>
        Vind-nivå
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          { lvl: 0, label: 'Stille' },
          { lvl: 1, label: 'Lite' },
          { lvl: 2, label: 'Moderat' },
          { lvl: 3, label: 'Mye' },
        ].map((w) => {
          const active = state.windLevel === w.lvl;
          // Wind icon scales with level (0=tiny, 3=biggest)
          const scaleMap = { 0: 0.5, 1: 0.75, 2: 1.0, 3: 1.3 };
          return (
            <button
              key={w.lvl}
              type="button"
              onClick={() => actions.setWindLevel(w.lvl)}
              className="py-3 rounded-md flex flex-col items-center gap-1 text-xs font-bold uppercase"
              style={{
                background: active ? '#FFD700' : '#1f2937',
                color: active ? '#030712' : '#94A3B8',
              }}
              data-testid={`wind-${w.lvl}`}
            >
              <Wind
                className="w-5 h-5"
                style={{
                  opacity: active ? 1 : 0.6,
                  transform: `scale(${scaleMap[w.lvl]})`,
                }}
              />
              {w.label}
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const TagsModule = ({ state, actions }) => {
  const customTagsByName = new Map(state.customTags.map((t) => [t.name, t]));
  // Combined list: defaults first, then customs (excluding any that match a default name)
  const allTags = [
    ...DEFAULT_TAGS.map((name) => ({ name, isDefault: true, id: `default-${name}` })),
    ...state.customTags
      .filter((t) => !DEFAULT_TAGS.includes(t.name))
      .map((t) => ({ ...t, isDefault: false })),
  ];
  return (
    <Card label="Hurtig-tagger">
      <div className="flex flex-wrap gap-2 mb-3">
        {allTags.map((t) => {
          const active = state.activeTags.includes(t.name);
          const isEditing = !t.isDefault && state.editingTagId === t.id;
          if (isEditing) {
            return (
              <div key={t.id} className="flex items-center gap-1">
                <input
                  type="text"
                  value={state.editTagValue}
                  onChange={(e) => actions.setEditTagValue(e.target.value)}
                  className="bg-[#1f2937] rounded-full px-3 py-1.5 text-xs text-white w-28"
                  data-testid={`edit-tag-input-${t.id}`}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') actions.saveEditTag();
                    if (e.key === 'Escape') actions.cancelEditTag();
                  }}
                />
                <button
                  type="button"
                  onClick={actions.saveEditTag}
                  className="p-1.5 rounded-full"
                  style={{ background: '#FFD700', color: '#030712' }}
                  data-testid={`save-edit-tag-${t.id}`}
                >
                  <Check className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={actions.cancelEditTag}
                  className="p-1.5 rounded-full"
                  style={{ background: '#374151', color: '#fff' }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          }
          return (
            <div key={t.id} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => actions.toggleTag(t.name)}
                className={`px-4 py-1.5 text-xs font-bold transition ${
                  !t.isDefault ? 'rounded-l-full' : 'rounded-full'
                }`}
                style={{
                  background: active ? '#FFD700' : '#1f2937',
                  color: active ? '#030712' : '#94A3B8',
                }}
                data-testid={`tag-${t.name}`}
              >
                {t.name}
              </button>
              {!t.isDefault && (
                <div
                  className="flex items-center gap-px rounded-r-full px-1.5"
                  style={{ background: active ? '#FFD700' : '#1f2937' }}
                >
                  <button
                    type="button"
                    onClick={() => actions.startEditTag(customTagsByName.get(t.name))}
                    className="p-1 hover:bg-black/10 rounded-full"
                    title="Rediger"
                    data-testid={`edit-tag-${t.name}`}
                  >
                    <Pencil
                      className="w-3 h-3"
                      style={{ color: active ? '#030712' : '#94A3B8' }}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.removeCustomTag(customTagsByName.get(t.name))}
                    className="p-1 hover:bg-black/10 rounded-full"
                    title="Slett"
                    data-testid={`delete-tag-${t.name}`}
                  >
                    <Trash2
                      className="w-3 h-3"
                      style={{ color: active ? '#7f1d1d' : '#ef4444' }}
                    />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {state.addingTag ? (
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Tagg-navn"
            value={state.newTagName}
            onChange={(e) => actions.setNewTagName(e.target.value)}
            className="flex-1 bg-[#1f2937] rounded-md px-3 py-2 text-sm text-white"
            data-testid="new-tag-name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') actions.addCustomTag();
            }}
          />
          <button
            type="button"
            onClick={actions.addCustomTag}
            className="px-4 py-2 rounded-md font-bold text-xs uppercase"
            style={{ background: '#FFD700', color: '#030712' }}
            data-testid="confirm-add-tag"
          >
            Legg til
          </button>
          <button
            type="button"
            onClick={() => actions.setAddingTag(false)}
            className="px-3 py-2 rounded-md text-xs uppercase"
            style={{ background: '#374151', color: '#fff' }}
          >
            Avbryt
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => actions.setAddingTag(true)}
          className="w-full py-2 text-[10px] font-extrabold tracking-widest uppercase rounded-md"
          style={{
            color: '#FFD700',
            border: '1px dashed #FFD700',
            background: 'transparent',
          }}
          data-testid="add-tag-btn"
        >
          + Legg til egen tagg
        </button>
      )}
    </Card>
  );
};

const TimerModule = ({ state, actions }) => (
  <Card label="Tidsbruk / Timer">
    <div className="relative">
      <div
        className="text-center text-5xl sm:text-6xl font-extrabold tabular-nums tracking-tight my-2"
        style={{ color: '#FFD700' }}
        data-testid="timer-display"
      >
        {formatTime(state.timerSeconds)}
      </div>
      <button
        type="button"
        onClick={actions.resetTimer}
        className="absolute top-2 right-0 p-2 rounded-md hover:bg-gray-700"
        title="Nullstill stoppeklokken"
        data-testid="timer-reset"
      >
        <RotateCcw className="w-4 h-4" style={{ color: '#94A3B8' }} />
      </button>
    </div>
    <div className="grid grid-cols-3 gap-2 mt-3">
      <button
        type="button"
        onClick={actions.startTimer}
        disabled={state.timerRunning}
        className="py-3 rounded-md font-bold text-sm uppercase disabled:opacity-50"
        style={{ background: '#10b981', color: '#030712' }}
        data-testid="timer-start"
      >
        Start
      </button>
      <button
        type="button"
        onClick={actions.splitTimer}
        disabled={!state.timerRunning}
        className="py-3 rounded-md font-bold text-sm uppercase disabled:opacity-50"
        style={{ background: '#374151', color: '#FFFFFF' }}
        data-testid="timer-split"
      >
        Splitt
      </button>
      <button
        type="button"
        onClick={actions.stopTimer}
        className="py-3 rounded-md font-bold text-sm uppercase"
        style={{
          background: 'transparent',
          color: '#ef4444',
          border: '1px solid #ef4444',
        }}
        data-testid="timer-stop"
      >
        Stopp
      </button>
    </div>
    {state.splitTimes.length > 0 && (
      <div className="mt-3 text-xs grid grid-cols-3 gap-1" style={{ color: '#94A3B8' }}>
        {state.splitTimes.map((t, i) => (
          <div key={i} className="tabular-nums">
            #{i + 1}: {formatTime(t)}
          </div>
        ))}
      </div>
    )}
  </Card>
);

// Build a smooth Catmull-Rom spline as cubic Bezier path
const buildSmoothPath = (pts) => {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }
  return d;
};

const TrendModule = ({ state, actions }) => {
  const points = state.trendData || [];
  const hasData = points.length > 0;

  // ---- Total score (from current session) ----
  const series = state.series || [];
  const totalPoeng = series.reduce((sum, s) => sum + (Number(s.poeng) || 0), 0);
  // Auto-detect series-max based on highest observed score (50 vs 100 vs 250 etc)
  const observedMax = series.reduce((m, s) => Math.max(m, Number(s.poeng) || 0), 0);
  let maxPerSerie = 50;
  if (observedMax > 100) maxPerSerie = 250;
  else if (observedMax > 50) maxPerSerie = 100;
  const totalMax = Math.max(series.length * maxPerSerie, maxPerSerie);
  const accuracy = totalMax > 0 ? Math.min(100, (totalPoeng / totalMax) * 100) : 0;

  // ---- Trend chart ----
  const w = 320;
  const h = 140;
  const paddingX = 6;
  const paddingY = 8;
  let yMin = 40;
  let yMax = 50;
  let smoothPath = '';
  let areaPath = '';
  if (points.length > 0) {
    const scores = points.map((p) => p.score);
    const dataMin = Math.min(...scores);
    const dataMax = Math.max(...scores);
    yMin = Math.floor(Math.min(40, dataMin - 1));
    yMax = Math.ceil(Math.max(50, dataMax + 1));
    if (yMin === yMax) yMax = yMin + 5;
    const xStep = points.length > 1 ? (w - paddingX * 2) / (points.length - 1) : 0;
    const coords = points.map((p, i) => ({
      x: paddingX + i * xStep,
      y: h - paddingY - ((p.score - yMin) / (yMax - yMin)) * (h - paddingY * 2),
    }));
    smoothPath = buildSmoothPath(coords);
    areaPath = `${smoothPath} L${(w - paddingX).toFixed(2)},${h - paddingY} L${paddingX.toFixed(2)},${h - paddingY} Z`;
  }
  const yMid = Math.round((yMin + yMax) / 2);
  const periods = [7, 30, 90];
  const firstDate = points[0]?.date
    ? new Date(points[0].date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })
    : '';

  return (
    <div className="space-y-4">
      {/* TOTAL SCORE (LIVE TRACKING) */}
      <Card label="Totalscore (Live tracking)">
        <div className="flex items-center gap-3">
          <div className="flex items-baseline gap-1 min-w-fit">
            <span
              className="text-4xl font-extrabold tabular-nums"
              style={{ color: '#FFD700' }}
              data-testid="diary-total-score"
            >
              {totalPoeng}
            </span>
            <span className="text-base" style={{ color: '#94A3B8' }}>
              / {totalMax}
            </span>
          </div>
          <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: '#1f2937' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, accuracy)}%`,
                background: 'linear-gradient(90deg, #B8860B 0%, #FFD700 60%, #FFE74C 100%)',
              }}
            />
          </div>
          <div
            className="px-2 py-1.5 rounded-md text-[10px] font-extrabold tracking-wider tabular-nums whitespace-nowrap"
            style={{
              background: 'rgba(255,215,0,0.15)',
              color: '#FFD700',
              border: '1px solid #FFD700',
            }}
            data-testid="diary-accuracy"
          >
            {accuracy.toFixed(1)}% ACC
          </div>
        </div>
      </Card>

      {/* PROGRESJONSGRAF */}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-extrabold tracking-widest uppercase" style={{ color: '#94A3B8' }}>
            Progresjonsgraf
          </div>
          <div className="flex gap-1">
            {periods.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => actions.setTrendPeriod(p)}
                className="px-2 py-0.5 rounded text-[10px] font-bold"
                style={{
                  background: state.trendPeriod === p ? '#FFD700' : 'transparent',
                  color: state.trendPeriod === p ? '#030712' : '#94A3B8',
                  border: state.trendPeriod === p ? 'none' : '1px solid #374151',
                }}
                data-testid={`trend-period-${p}`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        {!hasData ? (
          <div
            className="text-sm text-center py-10 rounded-md"
            style={{ color: '#94A3B8', background: '#0b1220', border: '1px dashed #374151' }}
            data-testid="diary-trend-empty"
          >
            Ingen treningsdata for valgt periode
          </div>
        ) : (
          <div className="relative" data-testid="diary-trend-chart">
          <div className="absolute left-0 top-0 bottom-4 flex flex-col justify-between text-[9px] tabular-nums" style={{ color: '#94A3B8' }}>
            <span>{yMax}</span>
            <span>{yMid}</span>
            <span>{yMin}</span>
          </div>
          <svg
            viewBox={`0 0 ${w} ${h}`}
            className="w-full ml-7"
            preserveAspectRatio="none"
            style={{ height: 140 }}
          >
            <defs>
              <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFD700" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#FFD700" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#trendArea)" />
            <path d={smoothPath} fill="none" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="flex justify-between text-[9px] mt-1 ml-7" style={{ color: '#94A3B8' }}>
            <span>{firstDate}</span>
            <span>I dag</span>
          </div>
        </div>
        )}
      </Card>
    </div>
  );
};

const ImagesModule = ({ state, actions }) => {
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!state.uploading) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear on actual leave (not bubbled from children)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (state.uploading) return;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      // Wrap to mimic the change-event shape handleImageUpload expects
      actions.handleImageUpload({ target: { files } });
    }
  };

  return (
    <Card label="Bildeopplasting">
      <label
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`block w-full py-6 rounded-md text-center text-xs cursor-pointer transition-colors ${
          isDragging ? 'bg-yellow-500/10' : ''
        }`}
        style={{
          border: isDragging ? '2px dashed #FFD700' : '1px dashed #374151',
          color: isDragging ? '#FFD700' : '#94A3B8',
        }}
        data-testid="diary-image-upload-dropzone"
      >
        <Camera className="inline w-4 h-4 mr-2" style={{ color: '#FFD700' }} />
        {state.uploading
          ? 'Laster opp...'
          : isDragging
            ? 'Slipp bildet her for å laste opp'
            : 'Last opp bilde av blink (klikk eller dra og slipp)'}
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={actions.handleImageUpload}
          className="hidden"
          disabled={state.uploading}
          data-testid="diary-image-upload"
        />
      </label>
      {state.images.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {state.images.map((img, idx) => (
            <div key={idx} className="relative group">
              <img
                src={img.startsWith('http') ? img : `data:image/jpeg;base64,${img}`}
                alt={`Blink ${idx + 1}`}
                className="w-full h-20 object-cover rounded"
              />
              <button
                type="button"
                onClick={() => actions.removeImage(idx)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                data-testid={`remove-image-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const NotesModule = ({ state, actions }) => (
  <Card label="Personlige notater">
    <textarea
      rows={4}
      value={state.notes}
      onChange={(e) => actions.setNotes(e.target.value)}
      placeholder="Beskriv følelsen eller trekk fra økta..."
      className="w-full bg-[#1f2937] rounded-md px-3 py-3 text-sm text-white placeholder-gray-500 resize-none"
      style={{ border: '1px solid #374151' }}
      data-testid="diary-notes"
    />
  </Card>
);

export default SkytedagbokModal;
